import "./style.less";
import { Col, Divider, Form, Input, Row, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Paragraph from "antd/lib/typography/Paragraph";
import { useLocation, useNavigate } from "react-router-dom";
import {
  checkIsValidPassword,
  checkIsValidPhone,
  getCompletePhoneNumber,
  IAuthRegisterForm,
  isValidEmail,
  OtpType,
  useDebounce,
  usePassportService,
} from "../../../@vendor/utils";
import {
  VuiButton,
  VuiFormCard,
  VuiFormItem,
  VuiFormLayout,
  VuiHelmet,
} from "../../../@vendor/components";
import {
  FacebookCircleIcon,
  GoogleIcon,
  TermsConditionRegisterModal,
} from "../../../components";
import PhoneInput from "../../../@vendor/components/atoms/PhoneInput";
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  errorFaultCode,
  localStorageKey,
} from "../../../constants";
import { IFormCustomValidation } from "../../../@vendor/utils/interfaces/form.interface";
import _ from "lodash";
import { CountryPhoneInputValue } from "antd-country-phone-input";
import { useLocalStorage } from "usehooks-ts";
import CheckIcon from "../../../components/icons/Check";
import { ValidateStatus } from "antd/lib/form/FormItem";
import { LoadingOutlined } from "@ant-design/icons";
import DangerIcon from "../../../components/icons/Danger";
import AuthRepository from "../../../repositories/AuthRepository";
import { AxiosError } from "axios";
import { otpProviders } from "../../../constants/auth.constant";
import { getGuestInfo } from "../../../@vendor/utils/helpers/local-storage.helper";

const endPoint = () => window._env_.REACT_APP_PASSPORT_END_POINT;

const AuthRegisterPage = () => {
  const { t } = useTranslation();
  const guest = getGuestInfo();
  const location = useLocation();
  const navigate = useNavigate();
  const { requestOtp, isOnRequestOtp } = usePassportService(true);
  const [form] = Form.useForm<IAuthRegisterForm>();
  const [showTermsConditionModal, setShowTermsConditionModal] =
    useState<boolean>(false);
  const [acceptAgreement, setAcceptAgreement] = useState<boolean>(false);
  const [requestOtpError, setReqeustOtpError] = useState<string>();
  const email = Form.useWatch("email", form) ?? "";
  const phone = Form.useWatch("phone_number", form) as CountryPhoneInputValue;
  const [emailValidation, setEmailValidation] = useState<IFormCustomValidation>(
    {
      validateStatus: undefined,
      errorMessage: undefined,
    }
  );
  const [phoneValidation, setPhoneValidation] = useState<IFormCustomValidation>(
    {
      validateStatus: undefined,
      errorMessage: undefined,
    }
  );
  const debounceEmail = useDebounce<string>(email, 500);
  const debouncePhone = useDebounce<CountryPhoneInputValue>(phone, 500);

  const [registerFormData, setRegisterFormData] = useLocalStorage<
    IAuthRegisterForm | undefined
  >(localStorageKey.authRegisterForm, undefined);

  const handleAgreement = useCallback(() => {
    setShowTermsConditionModal(false);
    setAcceptAgreement(true);
    form.submit();
  }, [form]);

  const onFinish = useCallback(
    async (values: IAuthRegisterForm) => {
      if (!acceptAgreement) {
        setShowTermsConditionModal(true);
        return;
      }

      await requestOtp(
        getCompletePhoneNumber(
          String(values?.phone_number?.code),
          values?.phone_number?.phone
        ),
        otpProviders[0],
        OtpType.REGISTER_OTP
      )
        .then(() => {
          setRegisterFormData(values);
          navigate(`/auth/verification-code?type=${OtpType.REGISTER_OTP}`);
        })
        .catch((err) => {
          const faultCode = _.get(err, "response.data.fault.code");
          if (faultCode === errorFaultCode.otpWaThrottled) {
            setReqeustOtpError(t("notification.error.otpThrottled"));
          } else {
            setReqeustOtpError(t("notification.error.default"));
          }
        });
    },
    [acceptAgreement, requestOtp, setRegisterFormData, navigate, t]
  );

  const handleCloseModal = useCallback(() => {
    setShowTermsConditionModal(false);
  }, []);

  const handleGoogleLogin = useCallback(async () => {
    window.location.href = `${endPoint()}/api/auth/google`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endPoint, location]);

  const handleFacebookLogin = useCallback(() => {
    window.location.href = `${endPoint()}/api/auth/facebook`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endPoint, location]);

  const isValidPhone = useCallback((phoneValue: CountryPhoneInputValue) => {
    const phoneLength = _.get(phoneValue, "phone.length", 0);

    return phoneLength >= 7 && phoneLength <= 15;
  }, []);

  const handleValidateEmail = useCallback(async () => {
    setEmailValidation((s) => ({
      ...s,
      validateStatus: "validating",
      errorMessage: undefined,
    }));

    await AuthRepository.checkEmail({
      email: email,
    })
      .then(() => {
        if (isValidEmail(email)) {
          setEmailValidation((s) => ({
            ...s,
            validateStatus: "success",
          }));
        } else {
          setEmailValidation((s) => ({
            ...s,
            validateStatus: undefined,
            errorMessage: undefined,
          }));
        }
      })
      .catch((error: AxiosError) => {
        if (error.name !== "CanceledError") {
          setEmailValidation((s) => ({
            ...s,
            validateStatus: "error",
            errorMessage: _.get(error, "response.data.fault.message"),
          }));
        }
      });
  }, [email]);

  const handleValidatePhone = useCallback(async () => {
    setPhoneValidation((s) => ({
      ...s,
      validateStatus: "validating",
      errorMessage: undefined,
    }));

    await AuthRepository.checkPhone({
      phone_number: `${String(phone.code)} ${phone.phone}`,
    })
      .then(() => {
        if (isValidPhone(phone)) {
          setPhoneValidation((s) => ({
            ...s,
            validateStatus: "success",
          }));
        } else {
          setPhoneValidation((s) => ({
            ...s,
            validateStatus: undefined,
            errorMessage: undefined,
          }));
        }
      })
      .catch((error) => {
        if (error.name !== "CanceledError") {
          setPhoneValidation((s) => ({
            ...s,
            validateStatus: "error",
            errorMessage: _.get(error, "response.data.fault.message"),
          }));
        }
      });
  }, [isValidPhone, phone]);

  useEffect(() => {
    if (guest) {
      emailValidation.validateStatus = "success";
    } else {

      if (isValidEmail(debounceEmail)) {
        handleValidateEmail();
      } else {
        setEmailValidation((s) => ({
          ...s,
          validateStatus: undefined,
          errorMessage: undefined,
        }));
      }
    }
  }, [debounceEmail, handleValidateEmail]);

  useEffect(() => {
    if (guest) {
      phoneValidation.validateStatus = "success";
    } else {

      if (isValidPhone(debouncePhone)) {
        handleValidatePhone();
      } else {
        setPhoneValidation((s) => ({
          ...s,
          validateStatus: undefined,
          errorMessage: undefined,
        }));
      }
    }
  }, [debouncePhone, handleValidatePhone, isValidPhone]);

  useEffect(() => {
    if (registerFormData) {
      if (!registerFormData.provider_id) {
        form.setFieldsValue({
          name: registerFormData.name,
          email: registerFormData.email,
          phone_number: registerFormData.phone_number,
          password: registerFormData.password,
        });
      }

      localStorage.removeItem(localStorageKey.authRegisterForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderValidationStatusIcon = (validationStatus?: ValidateStatus) => {
    if (validationStatus === "validating") {
      return <LoadingOutlined />;
    }

    if (validationStatus === "success") {
      return <CheckIcon />;
    }

    if (validationStatus === "error") {
      return <DangerIcon />;
    }

    return <div />;
  };

  useEffect(() => {
    if (guest) {
      form.setFieldsValue({
        name: guest.name,
        email: guest.email,
        phone_number: {
          code: guest.phone_number.code,
          phone: guest.phone_number.phone,
        },
      });
    }
  }, [form]);

  return (
    <div id="auth-register-page">
      <VuiHelmet title={t("common.text.register")} />

      <TermsConditionRegisterModal
        title={t("common.text.terms&condition")}
        visible={showTermsConditionModal}
        onSubmit={handleAgreement}
        onClose={handleCloseModal}
      />

      <VuiFormLayout form={form} onFinish={onFinish}>
        <VuiFormCard className="auth-register-wrapper">
          <h4 className="auth-register-title">{t("common.text.register")}</h4>
          <VuiFormItem
            rules={[
              {
                required: true,
                message: t("validation.required", {
                  item: t("common.form.fullName.label"),
                }),
              },
            ]}
            name={"name"}
            label={t("common.form.fullName.label")}
          >
            <Input placeholder={t("common.form.fullName.placeholder")} />
          </VuiFormItem>

          <VuiFormItem
            rules={[
              {
                required: true,
                message: t("validation.required", {
                  item: t("common.form.email.label"),
                }),
              },
              {
                type: "email",
                message: t("validation.type.email", {
                  item: t("common.form.email.label"),
                }),
              },
            ]}
            validateStatus={emailValidation.validateStatus}
            help={emailValidation.errorMessage}
            name={"email"}
            label={t("common.form.email.label")}
          >
            <Input
              placeholder={t("common.form.email.placeholder")}
              suffix={renderValidationStatusIcon(
                emailValidation.validateStatus
              )}
            />
          </VuiFormItem>
          <VuiFormItem
            initialValue={{
              code: DEFAULT_PHONE_COUNTRY_CODE,
            }}
            rules={[
              {
                validator: (_, value) => checkIsValidPhone(t, value),
              },
            ]}
            name={"phone_number"}
            required
            label={t("common.form.phone.label")}
            validateStatus={phoneValidation.validateStatus}
            help={phoneValidation.errorMessage}
          >
            <PhoneInput
              placeholder={t("common.form.phone.placeholder")}
              suffix={renderValidationStatusIcon(
                phoneValidation.validateStatus
              )}
            />
          </VuiFormItem>
          <VuiFormItem
            rules={[
              {
                required: true,
                message: t("validation.password"),
              },
              {
                min: 8,
                message: t("validation.password"),
              },
              {
                validator: (_, value) => checkIsValidPassword(t, value),
              },
            ]}
            name="password"
            label={t("common.form.password.label")}
          >
            <Input.Password
              placeholder={t("common.form.password.minCharacter")}
            />
          </VuiFormItem>
          {!!requestOtpError && (
            <div style={{ marginBottom: 24 }}>
              <Typography.Text type="danger">{requestOtpError}</Typography.Text>
            </div>
          )}
          <VuiButton
            label={t("common.button.register")}
            loading={isOnRequestOtp}
            buttonProps={{
              htmlType: "submit",
              type: "primary",
              disabled:
                emailValidation.validateStatus !== "success" ||
                phoneValidation.validateStatus !== "success",
            }}
          />

          <Paragraph className="register-with-text">
            {t("common.text.orSignUpWith")}
          </Paragraph>

          <Row gutter={[16, 16]}>
            <Col md={12} xs={24}>
              <VuiButton
                label={t("common.button.facebook")}
                buttonProps={{
                  onClick: handleFacebookLogin,
                  icon: <FacebookCircleIcon />,
                }}
              />
            </Col>
            <Col md={12} xs={24}>
              <VuiButton
                label={t("common.button.google")}
                buttonProps={{
                  onClick: handleGoogleLogin,
                  icon: <GoogleIcon />,
                }}
              />
            </Col>
          </Row>

          <Divider />

          <Paragraph className="register-with-text large">
            {t("common.text.alreadyHaveAnAccountMark")}
          </Paragraph>

          <VuiButton
            buttonProps={{
              onClick: () => navigate("/auth/login"),
            }}
            label={t("common.button.login")}
          />
        </VuiFormCard>
      </VuiFormLayout>
    </div>
  );
};

export default AuthRegisterPage;
